import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import QRCode from 'qrcode'
import { ApiClientError } from '@/lib/api/client'
import {
  createVeterinaryVisit,
  fetchVeterinaryCatalog,
  fetchVeterinaryVisit,
  type VeterinaryCatalog,
  type VeterinaryVisit,
} from './mascot-veterinary-api'
import styles from './MascotVeterinary.module.css'
import gameStyles from './MascotGame.module.css'
import { DEFAULT_MASCOT, type MascotConfig } from '../mascot-catalog'
import { VeterinaryScene } from './VeterinaryScene'

const price = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const errorText = (error: unknown) =>
  error instanceof ApiClientError
    ? Object.values(error.errors ?? {}).flat()[0] || error.message
    : 'Não foi possível consultar sua compra. Tente novamente.'

type Props = {
  campaignId: number
  name: string
  health: number
  disabled: boolean
  onPaid: () => Promise<void>
  mascot?: MascotConfig
}
export function MascotVeterinary(props: Props) {
  return <VeterinaryClinic key={props.campaignId} {...props} />
}
function VeterinaryClinic({ campaignId, name, health, disabled, onPaid, mascot }: Props) {
  const [catalog, setCatalog] = useState<VeterinaryCatalog | null>(null)
  const [visit, setVisit] = useState<VeterinaryVisit | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [qr, setQr] = useState('')
  const [copied, setCopied] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const active = useRef(false)
  const locked = useRef(false)
  const lookupBusy = useRef(false)
  const requestId = useRef<string | null>(null)
  const notified = useRef<string | null>(null)
  const onPaidRef = useRef(onPaid)
  const visitId = visit?.id
  const visitStatus = visit?.status
  useEffect(() => {
    onPaidRef.current = onPaid
  }, [onPaid])
  const accept = useCallback((next: VeterinaryVisit | null) => {
    setVisit(next)
    if (next?.status === 'paid' && next.fulfilled_at && notified.current !== next.id) {
      notified.current = next.id
      void onPaidRef.current().catch(() => {
        if (active.current)
          setError('Compra confirmada. Atualize o jogo para ver a saúde e os benefícios.')
      })
    }
  }, [])
  useEffect(() => {
    active.current = true
    const controller = new AbortController()
    void fetchVeterinaryCatalog(campaignId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return
        setCatalog(data)
        accept(data.visit)
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(errorText(cause))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => {
      active.current = false
      controller.abort()
    }
  }, [campaignId, attempt, accept])
  useEffect(() => {
    let canceled = false
    setQr('')
    setCopied(false)
    if (visit?.status === 'pending' && visit.pix_key) {
      void QRCode.toDataURL(visit.pix_key, { width: 240, margin: 2 })
        .then((url) => {
          if (!canceled) setQr(url)
        })
        .catch(() => {})
    }
    return () => {
      canceled = true
    }
  }, [visit?.status, visit?.pix_key])
  useEffect(() => {
    if (
      !visitId ||
      !visitStatus ||
      !['creating', 'creation_uncertain', 'pending'].includes(visitStatus)
    )
      return
    const controller = new AbortController()
    const started = Date.now()
    const id = visitId
    let timer: number | undefined
    async function poll() {
      if (controller.signal.aborted || Date.now() - started >= 300_000) return
      if (!document.hidden && !locked.current && !lookupBusy.current) {
        lookupBusy.current = true
        try {
          const next = await fetchVeterinaryVisit(campaignId, id, controller.signal)
          if (controller.signal.aborted) return
          accept(next)
          if (!['creating', 'creation_uncertain', 'pending'].includes(next.status)) return
        } catch {
          /* The existing receipt remains visible; manual lookup is available. */
        } finally {
          lookupBusy.current = false
        }
      }
      if (!controller.signal.aborted) timer = window.setTimeout(() => void poll(), 5000)
    }
    timer = window.setTimeout(() => void poll(), 5000)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [campaignId, visitId, visitStatus, accept])
  async function donate() {
    if (locked.current || disabled || !catalog?.sales_open) return
    locked.current = true
    setBusy(true)
    setError('')
    requestId.current ??= crypto.randomUUID()
    try {
      const next = await createVeterinaryVisit(campaignId, requestId.current)
      if (active.current) {
        accept(next)
        requestId.current = null
      }
    } catch (cause) {
      if (active.current) setError(errorText(cause))
      if (cause instanceof ApiClientError && cause.status >= 400 && cause.status < 500)
        requestId.current = null
    } finally {
      locked.current = false
      if (active.current) setBusy(false)
    }
  }
  async function check() {
    if (locked.current || lookupBusy.current || !visit) return
    locked.current = true
    setBusy(true)
    setError('')
    try {
      const next = await fetchVeterinaryVisit(campaignId, visit.id)
      if (active.current) accept(next)
    } catch (cause) {
      if (active.current) setError(errorText(cause))
    } finally {
      locked.current = false
      if (active.current) setBusy(false)
    }
  }
  const waiting =
    visit && ['creating', 'creation_uncertain', 'pending'].includes(visit.status)
  const completed = visit?.status === 'paid' && Boolean(visit.fulfilled_at)
  return (
    <section className={styles.clinic} aria-label="Veterinário do mascote">
      <header className={styles.welcome}>
        <span className={styles.clinicBadge}>
          <Icon icon="mdi:paw" /> CLÍNICA DO BEM
        </span>
        <h2>Pequeno cuidado. Grande coração.</h2>
      </header>
      <div className={styles.careRoom}>
        <VeterinaryScene
          mascot={{ ...(mascot ?? DEFAULT_MASCOT), name }}
          health={health}
          completed={completed}
        />
        <div className={styles.health}>
          <span className={styles.healthIcon}>
            <Icon icon="mdi:heart-pulse" />
          </span>
          <h3>Saúde de {name}</h3>
          <strong>
            {Math.round(health)}
            <small>%</small>
          </strong>
          <progress max={100} value={health} aria-label={`Saúde de ${name}`} />
          <span className={styles.healthTarget}>Consulta + remédio = 100%</span>
          <small>Após confirmar a compra.</small>
          <small className={styles.cycle}>
            <Icon icon="mdi:calendar-clock-outline" /> Cai aos poucos em 15 dias.
          </small>
        </div>
      </div>
      <ol className={styles.steps} aria-label="Como funciona a consulta">
        <li>
          <span className={styles.stepIcon}>
            <Icon icon="mdi:hand-heart" />
          </span>
          <div>
            <strong>1. Você ajuda</strong>
            <span>Compra em apoio à entidade.</span>
          </div>
        </li>
        <li>
          <span className={styles.stepIcon}>
            <Icon icon="mdi:medical-bag" />
          </span>
          <div>
            <strong>2. Seu pet melhora</strong>
            <span>Consulta e remédio virtuais.</span>
          </div>
        </li>
        <li>
          <span className={styles.stepIcon}>
            <Icon icon="mdi:medal-outline" />
          </span>
          <div>
            <strong>3. Você conquista</strong>
            <span>2 consultas pagas no mês = medalha.</span>
            <small>Na mesma campanha.</small>
          </div>
        </li>
      </ol>
      {loading && <p role="status">Preparando a recepção…</p>}
      {error && <p role="alert">{error}</p>}
      {!catalog && !loading && (
        <button
          className={gameStyles.secondaryButton}
          onClick={() => {
            setLoading(true)
            setError('')
            setAttempt((value) => value + 1)
          }}
        >
          Tentar novamente
        </button>
      )}
      {catalog && (
        <>
          <div className={styles.donation}>
            <div className={styles.donationHeading}>
              <span className={styles.donationIcon}>
                <Icon icon="mdi:hand-heart" />
              </span>
              <div>
                <strong>Sua compra apoia {catalog.institution_name}.</strong>
                <span>O cuidado do pet é um presente pelo seu apoio.</span>
              </div>
              <b className={styles.amount}>{price(catalog.amount_cents)}</b>
            </div>
            <div className={styles.benefits}>
              <span>
                <Icon icon="mdi:ticket-confirmation-outline" />
                {catalog.donation_only
                  ? 'Compra sem números da sorte'
                  : `${catalog.quantity} ${catalog.quantity === 1 ? 'número da sorte' : 'números da sorte'}`}
              </span>
              <small>Roleta e raspadinha seguem as regras da campanha.</small>
            </div>
          </div>
          {!catalog.sales_open && (
            <p role="status">
              Compras pausadas. Pedidos já iniciados podem ser consultados.
            </p>
          )}
          {!waiting && (
            <button
              className={gameStyles.primaryButton}
              disabled={disabled || busy || !catalog.sales_open}
              onClick={() => void donate()}
            >
              <Icon icon="mdi:hand-heart" />
              {busy
                ? 'Preparando sua compra…'
                : `Comprar por ${price(catalog.amount_cents)} e cuidar de ${name}`}
            </button>
          )}
          {requestId.current && !busy && (
            <button className={gameStyles.secondaryButton} onClick={() => void donate()}>
              Retomar a mesma compra
            </button>
          )}
          {visit && (
            <div className={styles.receipt} aria-label="Compra da consulta veterinária">
              <h3>Consulta com remédio · {price(visit.amount_cents)}</h3>
              {visit.status === 'paid' && visit.fulfilled_at ? (
                <p role="status" className={styles.success}>
                  <Icon icon="mdi:check-decagram" />
                  <span>
                    <strong>Compra confirmada para {catalog.institution_name}!</strong>
                    <span>Consulta concluída · saúde restaurada a 100%.</span>
                    <small>Acompanhe seus benefícios no perfil.</small>
                  </span>
                </p>
              ) : visit.status === 'pending' ? (
                <>
                  <p role="status">
                    Pague o Pix para confirmar sua compra e cuidar de {name}.
                  </p>
                  {qr && (
                    <img
                      src={qr}
                      alt={`QR Code Pix para comprar e apoiar ${catalog.institution_name}`}
                    />
                  )}
                  {visit.pix_key && (
                    <>
                      <label>
                        Código Pix copia e cola
                        <textarea readOnly value={visit.pix_key} />
                      </label>
                      <button
                        className={gameStyles.secondaryButton}
                        onClick={() => {
                          if (!navigator.clipboard) {
                            setError('Selecione e copie o código Pix acima.')
                            return
                          }
                          void navigator.clipboard
                            .writeText(visit.pix_key!)
                            .then(() => setCopied(true))
                            .catch(() =>
                              setError('Selecione e copie o código Pix acima.')
                            )
                        }}
                      >
                        {copied ? 'Código copiado' : 'Copiar código Pix'}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <p role="status">
                  {visit.status === 'creating'
                    ? 'Preparando o Pix da sua compra…'
                    : visit.status === 'creation_uncertain'
                      ? 'Pix em confirmação. Não pague novamente; consulte o pedido ou fale com o suporte.'
                      : visit.status === 'paid'
                        ? 'Compra confirmada. Consultando o cuidado do mascote…'
                        : 'Pix indisponível. Já pagou? Consulte antes de comprar novamente.'}
                </p>
              )}
              <details className={styles.orderDetails}>
                <summary>Código do pedido</summary>
                <small>{visit.id}</small>
              </details>
              <button
                className={gameStyles.secondaryButton}
                disabled={busy}
                onClick={() => void check()}
              >
                {busy ? 'Consultando…' : 'Consultar pagamento'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
